import type { StyleSpecification } from 'maplibre-gl';
import { rasterStyle as buildRaster, resolveMap, type ResolvedMap } from '@delivery/shared';

/**
 * 底图配置。
 *
 * ★ 换供应商靠环境变量,不改代码:
 *   - `VITE_MAP_TILE_URL` —— 栅格瓦片模板,或矢量样式的 style.json
 *   - `VITE_MAP_API_KEY`  —— 地址里用 `{key}` 占位
 *   - `VITE_MAP_ATTRIBUTION`
 *
 *   写死在源码里的话,换供应商要改两个 app、重新构建,
 *   而且没法按环境区分(测试环境不该打真实瓦片服务)。
 *
 * ★ 不配的话默认是 OSM 公共瓦片 —— **只能开发用**,
 *   它的使用政策禁止商业/高流量。生产构建时
 *   `scripts/check-map-tiles.mjs` 会拦下来,界面上也会显眼地标出来。
 *
 * 国内市场走 AmapProvider,由后端 mapConfig.provider 决定,
 * 那时渲染高德 JS 而不是 MapLibre。见 docs/08-architecture.md §8.3
 */

/*
 * ★ 服务端下发的底图配置。
 *
 *   `VITE_MAP_TILE_URL` 是**构建期**变量 —— 换一次底图要重新构建前端
 *   再发一次版。而「换底图」通常发生在账单来了、或者这家在这个国家的图
 *   不准的时候,那时候没人想发版。所以真正的配置放在后台,
 *   登录后随 me 一起下发,这里只留一个兜底。
 */
let fromServer: { tileUrl: string | null; attribution: string | null } | null = null;

/** 登录后拿到 me 时调用一次 */
export function setServerMap(t: { tileUrl: string | null; attribution: string | null } | null): void {
  fromServer = t?.tileUrl ? t : null;
}

export function mapConfig(): ResolvedMap {
  return resolveMap({
    // 后台配的优先;没配才回落到构建期变量
    tileUrl: fromServer?.tileUrl ?? import.meta.env['VITE_MAP_TILE_URL'] ?? '',
    attribution:
      fromServer?.attribution ?? import.meta.env['VITE_MAP_ATTRIBUTION'] ?? '',
    apiKey: import.meta.env['VITE_MAP_API_KEY'] ?? '',
    isProd: import.meta.env.PROD,
  });
}

/** 矢量样式直接把 style.json 的地址交给 MapLibre,栅格才需要自己拼 */
export function mapStyle(dark: boolean): StyleSpecification | string {
  const m = mapConfig();
  if (m.kind === 'style') return m.url;
  return buildRaster(m.url, m.attribution, dark) as StyleSpecification;
}

/** 兼容原来的调用点 */
export function rasterStyle(dark: boolean): StyleSpecification | string {
  return mapStyle(dark);
}
